import { skipToken, useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import React from 'react';

import * as api from '@gitbook/api-client';
import type { RevisionPage } from '@gitbook/hive-core';
import { serialize } from '@gitbook/slate';
import { Icon } from '@gitbook/spine-icons';
import { AtomButton, Button, renderIcon, toast, Tooltip } from '@gitbook/spine-react';
import { Logger } from '@gitbook/util-debug';
import { useEventCallback } from '@gitbook/util-react-hooks';
import type { AgentOrganizationRpc } from '@services/content-agents/browser';

import { useNavigation } from '../../navigation';
import { apiQuery, apiQueryClient } from '../../state';
import {
    agentIsProcessing,
    type GitBookAgentClient,
    useAgentState,
    selectAgentEditTarget,
    type OrganizationAgentLocation,
} from '../Agent';
import {
    type AgentEditTarget,
    EditTargetHeader,
    EditTargetTray,
    getAgentEditTarget,
    getEditTargetNavigationTarget,
    PageIndicator,
    SelectionIndicator,
    useEditTargetDetails,
} from '../AgentChat';
import { isPageOfTarget, isSameEditTarget } from './locationTarget';
import { type AgentDockAnalyticsContext, useAgentDockEditor, useAgentDockPage } from './state';
import { type AgentSelectionReference, useSelectionSnapshot } from './useSelectionSnapshot';
import { useStartAgentConversation } from './useStartAgentConversation';

const logger = Logger('app:agent-dock');

/**
 * Why the target cannot be switched while a response runs. The response built its scope when it
 * started and edits through it until it ends, so switching under it would rename the tray while the
 * edits keep landing where they were going — and hide them, since the tray lists the edits of the
 * target it names.
 */
const SWITCH_BLOCKED_WHILE_RESPONDING = 'Wait for the agent to finish responding.';

/**
 * The page open in the editor and the text selected in it, as context of the target a conversation
 * edits in.
 *
 * Context of that target rather than things of their own: a page is only offered while it is a page
 * of the target, which is why the two are shown nested under it in the tray. And sent with each
 * message rather than settled with the conversation — they follow the user around, and a reply like
 * "fix this heading" means the page they are reading now, not the one they started on.
 */
export type AgentTargetContext = {
    /** The page open in the editor, when the user has one. */
    page?: RevisionPage;
    /** The text selected in that page's editor, when there is any. */
    selection?: AgentSelectionReference;
};

/**
 * What the user's location offers a new conversation as context.
 *
 * The target being viewed is the whole of it; the page and the selection are context of that target
 * rather than things of their own, which is why they are nested here and shown nested in the banner.
 * Anything else worth offering — the site being looked at, say — goes alongside.
 */
export type AgentLocationContext = AgentTargetContext & {
    /** Where the conversation would edit. */
    target: AgentEditTarget;
};

/**
 * A piece of the context the user can take out on its own.
 *
 * `target` is the whole banner: taking it out takes the page and the selection with it, since where
 * the user is stops being the conversation's business at all.
 */
export type AgentLocationContextPart = 'target' | 'page' | 'selection';

/**
 * Which parts of the context go out with a message: the target with the first one, the page and the
 * selection with every one.
 */
export type AgentLocationContextInclusion = Record<AgentLocationContextPart, boolean>;

/**
 * How the user's location differs from where the agent works, once the agent works somewhere.
 *
 * - `editable`: the user is in a target that is not the agent's, which they can switch the
 *   conversation to: another change request, or a space edited live. `target` is that target.
 * - `readonly`: the user is in a space but in none of its targets — its main content, read-only —
 *   where the agent's changes do not show.
 *
 * Both carry `agentTarget`, since the mismatch only exists once the agent works somewhere.
 */
export type UserTargetMismatch =
    | { kind: 'editable'; agentTarget: AgentEditTarget; target: AgentEditTarget }
    | { kind: 'readonly'; agentTarget: AgentEditTarget };

/**
 * The context a conversation would start with, which of it goes on with every message, and which
 * parts of it the user wants in.
 *
 * Read from where the user is rather than captured when the dock opens: the offer belongs to the
 * location, so walking to another change request or another page changes it and walking away
 * withdraws it. Sending the first message is what turns the target into the conversation's own —
 * from then on the agent is working in it, there is nothing left to offer and nothing left to take
 * back. The offer ends at the send itself, not at the server's echo of the message, so the banner
 * leaves as the message appears rather than a round-trip later.
 *
 * The page and the selection outlive the offer: they go out with every message, so a reply resolves
 * to the page the user is reading when they write it. Once the target is the agent's they are read
 * against it rather than against the location — the user may have walked on to another change
 * request since, and a page of that one is not a page of the one the agent is writing in.
 */
export function useLocationContext(input: {
    agent: GitBookAgentClient<AgentOrganizationRpc> | null;
    location: OrganizationAgentLocation;
    /**
     * Whether the conversation has been written in, so the target belongs to it rather than to the
     * location. Answered from what the conversation is known to hold — not from what has arrived
     * so far, which is false for every conversation until its first transcript lands.
     */
    started: boolean;
}): {
    /** What the location offers, or null when it offers nothing — or once the offer is settled. */
    offered: AgentLocationContext | null;
    /**
     * The page and the selection in the target the agent is working in, once the first message has
     * made it the conversation's own. Null until then, and while the user is not in that target.
     */
    attached: AgentTargetContext | null;
    /** Where the agent works, once it has a target. */
    agentTarget: AgentEditTarget | null;
    /**
     * How the user's location differs from where the agent works. Null when the user is in the
     * agent's target, when the agent has none yet, on screens with no space, and while the space or
     * the comparison is still loading.
     */
    userTargetMismatch: UserTargetMismatch | null;
    included: AgentLocationContextInclusion;
    toggle: (part: AgentLocationContextPart) => void;
    /** The included page and selection, in the shape a message carries them. */
    messageContext: api.AIMessageContext | undefined;
    /** Hand the target to the conversation. Call before posting the first message. */
    send: () => Promise<void>;
    /** Make the user's target the conversation's. Rejects when the agent refuses it. */
    switchTarget: () => Promise<void>;
    /** Why the switch has to wait, while it does: a response is running. Null when it may go ahead. */
    switchBlocked: string | null;
} {
    const { agent, location, started } = input;
    const { spaceId, changeRequest } = location;

    // Only asked about a space the user is standing in outside a change request, which is the one
    // case where the answer decides anything: the space is the target only if it is edited live.
    // Deliberately without `nullOnError`, which `getAPIQueryKey` folds into the query key: with it
    // this would sit on a key of its own rather than the one the space screen fills, and cost a
    // second request (and a second realtime subscription) for a space already in the cache. A space
    // out of reach surfaces as `isError` instead, which reads the same as not offering it.
    const spaceQuery = useQuery({
        ...apiQuery(
            'getSpaceById',
            spaceId && changeRequest === undefined ? { spaceId } : skipToken
        ),
        throwOnError: false,
    });
    const space = spaceQuery.data;
    const spaceIsEditedLive = Boolean(
        space && space.editMode === api.SpaceEditMode.Live && space.permissions.edit
    );
    // Whether the answer is still on its way, which `send` has to wait for: the change-request
    // target came straight off the route, but this one does not exist until the space has loaded.
    const spaceIsPending = Boolean(spaceId) && changeRequest === undefined && spaceQuery.isPending;

    // Where the agent says it is writing. Selected as reported and converted in a memo: the
    // conversion mints an object, and a snapshot that changes on every read never settles.
    const reportedTarget = useAgentState(agent, selectAgentEditTarget);
    const attachedTarget = React.useMemo(
        () => getAgentEditTarget(reportedTarget),
        [reportedTarget]
    );

    // The first submit ends the offer phase right then — the banner leaves as the message appears —
    // rather than when the server echoes the message back, which is `started` and a round-trip
    // later. `started` still matters on its own: a restored conversation arrives written in and
    // with no submit.
    const [settled, setSettled] = React.useState(false);
    // Whether the target is the conversation's own by now, rather than the location's to offer.
    // A conversation editing somewhere has one whatever else is known about it — the agent only
    // attaches a target by working in it — and offering it the target it already writes in puts
    // the same change request on screen twice, once to accept and once to open.
    const targetSettled = started || settled || attachedTarget !== null;

    // What the location would have the conversation edit in: the change request the URL addresses,
    // or the space itself. Not yet a target — a space is only one while edited live, which the
    // offer checks below — but where a page has to be to count as this location's either way.
    const locationTarget = React.useMemo((): AgentEditTarget | null => {
        if (!spaceId) {
            return null;
        }
        return changeRequest !== undefined
            ? { kind: 'change_request', spaceId, changeRequest }
            : { kind: 'space', spaceId };
    }, [spaceId, changeRequest]);

    // The change request the agent works in, as the API describes it, to tell it apart from the one
    // the URL addresses: the URL usually carries the number and the agent always the id. On the key
    // the tray naming the target already fills, so this costs no request of its own.
    const { data: attachedChangeRequest } = useQuery({
        ...apiQuery(
            'getChangeRequestById',
            attachedTarget?.kind === 'change_request'
                ? {
                      spaceId: attachedTarget.spaceId,
                      changeRequestId: String(attachedTarget.changeRequest),
                  }
                : skipToken
        ),
        retry: false,
        throwOnError: false,
    });

    // Where a page has to be to be offered. The location until the first message, since that is
    // what the message attaches; what the agent reports from then on — the user may have walked to
    // another change request since, and its pages are not pages of the one the agent writes in.
    const pageTarget = targetSettled ? attachedTarget : locationTarget;

    const publishedPage = useAgentDockPage();
    const publishedEditor = useAgentDockEditor();
    // Only the page of that target: an overlay screen can have published its own while the location
    // still describes the screen underneath it. Compared on the change request too — neither side
    // has one for a space edited live, which is what matches there.
    const pageContext =
        publishedPage && isPageOfTarget(publishedPage, pageTarget) ? publishedPage : null;
    const page = pageContext?.page ?? null;
    // And only the selection of an editor mounted for that page, which arrives later than the page
    // itself — the page comes with the revision, the editor once the document has loaded.
    const editor = page && publishedEditor?.pageId === page.id ? publishedEditor.editor : null;
    const { selectionSnapshot } = useSelectionSnapshot({ editor, pageId: page?.id });

    // The target the location offers, once its space is known to take one: the change request the
    // URL addresses, or the space itself while it is edited live.
    const locationEditTarget =
        locationTarget && (locationTarget.kind === 'change_request' || spaceIsEditedLive)
            ? locationTarget
            : null;

    // Derived rather than held in state: state would have to be told when the user moves, and the
    // answer is already in the location.
    const offered = React.useMemo((): AgentLocationContext | null => {
        if (targetSettled || !locationEditTarget) {
            return null;
        }

        return {
            target: locationEditTarget,
            ...(page ? { page } : {}),
            ...(selectionSnapshot ? { selection: selectionSnapshot } : {}),
        };
    }, [targetSettled, locationEditTarget, page, selectionSnapshot]);

    // What goes on being offered once the target is the agent's. Null rather than empty when the
    // user is not in that target, so the tray has nothing to make room for.
    const attached = React.useMemo((): AgentTargetContext | null => {
        if (!targetSettled || !page) {
            return null;
        }
        return { page, ...(selectionSnapshot ? { selection: selectionSnapshot } : {}) };
    }, [targetSettled, page, selectionSnapshot]);

    // Withheld while the user's target and the agent's cannot be told apart yet — the URL's number
    // against the agent's id waits on the lookup above — rather than offered on a guess that would
    // invite the user to switch to where the agent already is. Likewise while the space loads, until
    // when the location is not known to offer nothing, and on screens with no space, where there is
    // nothing to be mistaken about.
    const userTargetMismatch = React.useMemo((): UserTargetMismatch | null => {
        if (!targetSettled || !attachedTarget || !locationTarget) {
            return null;
        }
        if (locationEditTarget) {
            return isSameEditTarget(locationEditTarget, attachedTarget, attachedChangeRequest) ===
                false
                ? { kind: 'editable', agentTarget: attachedTarget, target: locationEditTarget }
                : null;
        }
        return spaceIsPending ? null : { kind: 'readonly', agentTarget: attachedTarget };
    }, [
        targetSettled,
        attachedTarget,
        locationTarget,
        locationEditTarget,
        attachedChangeRequest,
        spaceIsPending,
    ]);

    // The note stays up meanwhile — the user is still somewhere else — only the switch waits.
    const processing = useAgentState(agent, agentIsProcessing);
    const switchBlocked = processing ? SWITCH_BLOCKED_WHILE_RESPONDING : null;

    // Answers about this conversation rather than about the change request or page they were given
    // on: putting a part back is a click away, so nothing is gained by remembering what the user
    // turned down where.
    const [hidden, setHidden] = React.useState<Partial<Record<AgentLocationContextPart, boolean>>>(
        {}
    );
    // Plain values rather than a reader function: a `useEventCallback` sees the render it was
    // created in, so asking it during this one answers with the state before the last click.
    const included: AgentLocationContextInclusion = {
        target: !hidden.target,
        page: !hidden.page,
        selection: !hidden.selection,
    };
    const toggle = useEventCallback((part: AgentLocationContextPart) => {
        setHidden((previous) => ({ ...previous, [part]: !previous[part] }));
    });

    // The offered target, waiting for the space lookup when that is all it is missing. Only the
    // live-space target can be pending — a change request is read straight off the route — so this
    // resolves immediately in every other case.
    const resolveTarget = useEventCallback(async (): Promise<AgentEditTarget | null> => {
        if (offered) {
            return offered.target;
        }
        if (targetSettled || !spaceIsPending || !spaceId) {
            return null;
        }

        const fetched = await apiQueryClient
            .ensureQueryData(apiQuery('getSpaceById', { spaceId }))
            .catch(() => null);

        return fetched?.editMode === api.SpaceEditMode.Live && fetched.permissions.edit
            ? { kind: 'space', spaceId }
            : null;
    });

    // Built from the published page rather than from `offered`, which additionally waits on the
    // space lookup: the page and the selection are known synchronously, and a user who submits
    // before that lookup lands should still have the paragraph they highlighted reach the agent.
    // The page is only ever published from a surface the user can edit, so it implies a target.
    const messageContext = React.useMemo((): api.AIMessageContext | undefined => {
        // Taking the target out of the offer takes the page and the selection with it. Only while it
        // is on offer, though: once the target is the agent's, the page is a page of it whatever was
        // decided about the offer.
        if (!pageContext || (!targetSettled && hidden.target)) {
            return undefined;
        }

        const includedPage = hidden.page ? undefined : pageContext.page;
        const selection = hidden.selection ? undefined : selectionSnapshot;
        if (!includedPage && !selection) {
            return undefined;
        }

        return {
            // Carried whenever a selection is, even with the page itself hidden: a selection is a
            // range inside that page's document, and the agent resolves one through the other.
            location: { spaceId: pageContext.spaceId, pageId: pageContext.page.id },
            ...(selection
                ? { selection: { ...serialize(selection.range), content: selection.text } }
                : {}),
        };
    }, [pageContext, targetSettled, hidden, selectionSnapshot]);

    // Point the conversation at a target, over the same RPCs the agent's own tool goes through. The
    // agent resolves whichever identifier the URL carried to the change request it addresses.
    const attachTarget = useEventCallback(async (target: AgentEditTarget) => {
        if (!agent) {
            return;
        }
        if (target.kind === 'change_request') {
            await agent.actions.attachChangeRequest({
                spaceId: target.spaceId,
                changeRequest: target.changeRequest,
            });
        } else {
            await agent.actions.attachSpace({ spaceId: target.spaceId });
        }
    });

    const send = useEventCallback(async () => {
        // Resolved before the offer is latched shut: a live-edit space only becomes a target once
        // its lookup lands, and the user can submit before then — latching first would drop the
        // attach for good, since `settled` also stops the offer ever coming back. The message
        // delivery already waits on this call, so waiting here costs nothing it did not already.
        const target = await resolveTarget();

        setSettled(true);

        if (!target || hidden.target) {
            return;
        }

        try {
            await attachTarget(target);
        } catch (error) {
            // The message matters more than the context it would have carried: it goes out either
            // way, and an agent that finds nothing attached opens a change request of its own.
            logger.error('Failed to attach the edit target in context', error);
        }
    });

    // Left to throw, unlike `send`: the switch is the user's own action, so a refusal is theirs to
    // hear about rather than something to log and move past.
    const switchTarget = useEventCallback(async () => {
        if (userTargetMismatch?.kind !== 'editable') {
            return;
        }
        await attachTarget(userTargetMismatch.target);
    });

    return {
        offered,
        attached,
        agentTarget: attachedTarget,
        userTargetMismatch,
        included,
        toggle,
        messageContext,
        send,
        switchTarget,
        switchBlocked,
    };
}

/**
 * The context the conversation is about to start with, shown above the composer.
 */
export function LocationContextBanner(props: {
    context: AgentLocationContext;
    included: AgentLocationContextInclusion;
    onToggle: (part: AgentLocationContextPart) => void;
}) {
    const { context, included, onToggle } = props;
    const { target } = context;
    const { icon, label, number, noun } = useEditTargetDetails(target);

    return (
        <EditTargetTray
            // Hidden rather than unmounted, so taking the context out slides the tray behind the
            // composer and putting it back slides it out again.
            hidden={!included.target}
        >
            <EditTargetHeader
                icon={icon}
                label={label}
                {...(number !== undefined ? { number } : {})}
            >
                <AtomButton
                    onPress={() => onToggle('target')}
                    aria-label={`Remove ${noun} from context`}
                    className="pointer-events-auto flex size-4 items-center justify-center rounded-xs text-base opacity-60 transition-opacity hover:opacity-100"
                >
                    <Icon.X className="size-3.5" />
                </AtomButton>
            </EditTargetHeader>
            {context.page || context.selection ? (
                <LocationContextChips context={context} included={included} onToggle={onToggle} />
            ) : null}
        </EditTargetTray>
    );
}

/**
 * The analytics report the conversation is about to start with, shown above the composer.
 */
export function AnalyticsContextBanner(props: { analytics: AgentDockAnalyticsContext }) {
    const { analytics } = props;

    return (
        <EditTargetTray>
            <EditTargetHeader
                icon={renderIcon(analytics.icon, {
                    className: 'size-3.5 shrink-0 text-neutral-high',
                })}
                label={`${analytics.label} · ${analytics.detail}`}
            />
        </EditTargetTray>
    );
}

/**
 * The page and the selection, as a row of chips under the target they are context of.
 *
 * Shown in whichever tray names that target: the one the conversation starts with, and the one the
 * agent's own target takes over once the first message has attached it. Each chip can be taken out
 * of the context and put back.
 */
export function LocationContextChips(props: {
    context: AgentTargetContext;
    included: Pick<AgentLocationContextInclusion, 'page' | 'selection'>;
    onToggle: (part: Exclude<AgentLocationContextPart, 'target'>) => void;
}) {
    const { context, included, onToggle } = props;

    return (
        // One row, never two: the window is narrow and the chips sit above a composer, so they
        // truncate instead of pushing it down. Half the row is reserved for each when both are
        // there, or a long selection would squeeze the page down to its icon.
        <div
            className={clsx(
                'flex min-w-0 gap-1.5',
                context.page && context.selection && '*:max-w-[50%]'
            )}
        >
            <PageIndicator
                page={context.page}
                isHidden={!included.page}
                onToggle={() => onToggle('page')}
                fill
                // The tray behind them is already grey, so the chips take the page's own surface to
                // stand off it.
                className="bg-base"
            />
            <SelectionIndicator
                selectedText={context.selection?.text}
                isHidden={!included.selection}
                onToggle={() => onToggle('selection')}
                fill
                className="bg-base"
            />
        </div>
    );
}

/**
 * A note under the target the agent works in, for when the user has walked to another one, with a
 * way to bring the agent along or to start a conversation there.
 *
 * In the place the page and selection chips take while the user is in the agent's target: the chips
 * are context of that target, and the user standing somewhere else is exactly why there are none to
 * show.
 */
export function LocationContextSwitch(props: {
    /** Where the agent works. */
    agentTarget: AgentEditTarget;
    /** Where the user is. */
    target: AgentEditTarget;
    /** The site the user is in, which the targets' spaces are looked up in first. */
    siteId: string | undefined;
    /** Make it the conversation's target. Rejects when the agent refuses. */
    onSwitch: () => Promise<void>;
    /** Why the switch has to wait, when it does: the button is disabled, and says so on hover. */
    blocked: string | null;
}) {
    const { agentTarget, target, siteId, onSwitch, blocked } = props;
    const agent = useEditTargetDetails(agentTarget);
    const viewed = useEditTargetDetails(target);
    const startConversation = useStartAgentConversation();
    const viewedName = viewed.number !== undefined ? `#${viewed.number}` : `this ${viewed.noun}`;
    // Change request numbers repeat from one space to the next, so the same number in a different
    // space has to say that it is not the one in front of the user. In a site its spaces are
    // sections of it, and the space itself is secondary.
    const sameCRNumberInOtherSpace =
        agent.number !== undefined &&
        agent.number === viewed.number &&
        agentTarget.spaceId !== target.spaceId;
    const agentName = `${agent.number !== undefined ? `#${agent.number}` : `the ${agent.noun}`}${
        sameCRNumberInOtherSpace ? ` in another ${siteId ? 'site section' : 'space'}` : ''
    }`;

    return (
        <div className="flex min-w-0 flex-col gap-1.5 pl-2 text-ui-small text-neutral-low">
            <span>
                Changes here are in {agentName} while you're viewing {viewedName}.
            </span>
            <div className="flex flex-wrap gap-1.5">
                <Tooltip {...(blocked ? { label: blocked } : { disabled: true })}>
                    {/* Wrapped in a span for the tooltip to hold on to: a disabled button swallows the
                        hover the tooltip needs, so the button gives up its pointer events and the span
                        takes them. */}
                    <span className={clsx('flex', blocked && 'cursor-not-allowed')}>
                        <Button
                            kind="secondary"
                            size="xsmall"
                            label={`Switch to ${viewedName} in this chat`}
                            disabled={blocked !== null}
                            // Returned so the button shows the switch in progress; the toast says why
                            // when the agent refuses — a space whose live edits are locked, a change
                            // request the user may not edit.
                            onPress={() =>
                                toast.catch(onSwitch, `Could not switch to the ${viewed.noun}`)
                            }
                            className={clsx(blocked && 'pointer-events-none')}
                        />
                    </span>
                </Tooltip>
                {startConversation ? (
                    <Button
                        kind="secondary"
                        size="xsmall"
                        label={`Open a new chat for ${viewedName}`}
                        onPress={() => startConversation({})}
                    />
                ) : null}
            </div>
        </div>
    );
}

/**
 * A note under the target the agent works in, for when the user is in its space but not in the
 * target, with a link to it. The agent's changes are in the target, so what the user is looking at
 * does not show them.
 */
export function LocationContextAway(props: {
    /** Where the agent works. */
    target: AgentEditTarget;
}) {
    const { target } = props;
    const { number, noun } = useEditTargetDetails(target);
    const { navigate } = useNavigation();
    const note = `Changes are visible in ${number !== undefined ? `#${number}` : `the ${noun}`}, which is not open on this screen.`;

    return (
        <div className="flex min-w-0 items-center gap-2 pl-2 text-ui-small text-neutral-low">
            <Tooltip label={note}>
                <span className="min-w-0 flex-1 truncate">{note}</span>
            </Tooltip>
            <Button
                kind="link"
                size="small"
                label={number !== undefined ? `Open #${number}` : `Open ${noun}`}
                onPress={() => navigate(getEditTargetNavigationTarget(target))}
                className="shrink-0"
            />
        </div>
    );
}

/**
 * Composer control taking the location's context out of the conversation, and putting it back.
 *
 * Sits beside the attach control because it is the same kind of decision — what goes out with the
 * message — and because the banner's own control can only ever remove.
 */
export function LocationContextToggle(props: {
    context: AgentLocationContext;
    included: boolean;
    onToggle: () => void;
}) {
    const { context, included, onToggle } = props;
    const { target } = context;
    const name =
        target.kind === 'change_request' ? `change request #${target.changeRequest}` : 'this space';

    return (
        <Button
            kind="blank"
            size="small"
            leadingIcon={target.kind === 'change_request' ? Icon.ChangeRequest : Icon.Space}
            iconOnly
            active={included}
            label={included ? `Remove ${name} from the context` : `Add ${name} to the context`}
            onPress={onToggle}
        />
    );
}
